import React, { useCallback, useMemo, useState } from "react";
import { X } from "lucide-react";
import type { FacilityTimeCounts, FacilityTimeRow, ReportedChartType } from "../types";
import type { OrgUnitHierarchy, OrgUnitOption } from "@/components/org-unit/OrgUnitTree";
import { anomalyColor, formatPeriodLabel, formatSeverity, toFiniteNumber } from "../utils";
import { useLanguage } from "@/i18n/LanguageContext";

export const FacilityTimeCountCards: React.FC<{ counts: FacilityTimeCounts }> = ({ counts }) => {
  const { t } = useLanguage();
  const items = [
    { label: t("dash.count.trend"), value: counts.trend ?? 0, tone: "border-cyan-200/60 bg-cyan-200/40 text-cyan-700" },
    { label: t("dash.count.rolling"), value: counts.rolling ?? 0, tone: "border-sky-200/60 bg-sky-200/40 text-sky-700" },
    { label: t("dash.count.seasonal"), value: counts.seasonal ?? 0, tone: "border-violet-200/60 bg-violet-200/40 text-violet-700" },
    // { label: "Data Quality Flags", value: counts.data_quality ?? 0, tone: "border-red-200/60 bg-red-200/40 text-red-700" },
    { label: t("dash.count.missing"), value: counts.missing_data ?? 0, tone: "border-amber-200/60 bg-amber-200/40 text-amber-700" },
  ];

  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
      {items.map((item) => (
        <div key={item.label} className={`rounded-xl border bg-white px-3 py-2 shadow-soft ${item.tone}`}>
          <p className="text-[9px] font-medium uppercase tracking-wider text-slate-500">{item.label}</p>
          <p className="mt-0.5 text-sm font-bold">{item.value}</p>
        </div>
      ))}
    </div>
  );
};

export const FacilityTimeChart: React.FC<{
  seriesRows: FacilityTimeRow[];
  methodRows: FacilityTimeRow[];
  anomalyRows: FacilityTimeRow[];
  orgUnitOptions?: OrgUnitOption[];
  indicator?: string;
  anomHierarchy?: OrgUnitHierarchy;
  anomYear?: unknown;
  anomMonth?: unknown;
}> = React.memo(({ seriesRows, methodRows, anomalyRows, orgUnitOptions, indicator, anomHierarchy }) => {
  const { t } = useLanguage();
  const unit = seriesRows[0]?.unit || "";
  const [selectedAnomaly, setSelectedAnomaly] = useState<FacilityTimeRow | null>(null);

  const methodByPeriod = useMemo(() => {
    const map = new Map<string, Record<string, FacilityTimeRow>>();
    methodRows.forEach((row) => {
      if (!row.period || !row.anomaly_type) return;
      const current = map.get(row.period) || {};
      current[row.anomaly_type] = row;
      map.set(row.period, current);
    });
    return map;
  }, [methodRows]);

  const chartRows = useMemo(() => seriesRows.map((row) => {
    const methods = methodByPeriod.get(row.period || "") || {};
    return {
      ...row,
      periodLabel: formatPeriodLabel(row.period),
      value: toFiniteNumber(row.value),
      trendExpected: toFiniteNumber(methods.trend?.expected),
      rollingExpected: toFiniteNumber(methods.rolling?.expected),
      seasonalExpected: toFiniteNumber(methods.seasonal?.expected),
    };
  }), [seriesRows, methodByPeriod]);

  const anomalies = useMemo(() => anomalyRows.filter(
    (row) =>
      (row.anomaly_type === "trend" || row.anomaly_type === "rolling" || row.anomaly_type === "seasonal") &&
      row.value !== undefined &&
      row.value !== null,
  ), [anomalyRows]);

  const { svgWidth, svgHeight, chartMargin, plotWidth, plotHeight, yMin, yMax } = useMemo(() => {
    const w = Math.max(720, chartRows.length * 54);
    const h = 280;
    const m = { top: 18, right: 24, bottom: 42, left: 54 };
    const pw = w - m.left - m.right;
    const ph = h - m.top - m.bottom;
    const numericValues = chartRows.flatMap((row) =>
      [row.value, row.trendExpected, row.rollingExpected, row.seasonalExpected].filter((value): value is number => value !== null),
    );
    const minVal = numericValues.length ? Math.min(...numericValues) : 0;
    const maxVal = numericValues.length ? Math.max(...numericValues) : 1;
    const padding = Math.max((maxVal - minVal) * 0.12, 1);
    return { svgWidth: w, svgHeight: h, chartMargin: m, plotWidth: pw, plotHeight: ph, yMin: minVal - padding, yMax: maxVal + padding };
  }, [chartRows]);

  const xForIndex = useCallback((index: number) =>
    chartMargin.left + (chartRows.length <= 1 ? plotWidth / 2 : (index / (chartRows.length - 1)) * plotWidth),
  [chartMargin.left, plotWidth, chartRows.length]);

  const yForValue = useCallback((value: number) =>
    chartMargin.top + ((yMax - value) / Math.max(yMax - yMin, 1)) * plotHeight,
  [chartMargin.top, yMax, yMin, plotHeight]);

  const pointForPeriod = useMemo(() =>
    new Map(chartRows.map((row, index) => [row.period, { x: xForIndex(index), row }])),
  [chartRows, xForIndex]);

  const linePath = useCallback((key: "value" | "trendExpected" | "rollingExpected" | "seasonalExpected") => {
    let started = false;
    return chartRows
      .map((row, index) => {
        const value = row[key];
        if (value === null) return "";
        const command = started ? "L" : "M";
        started = true;
        return `${command} ${xForIndex(index).toFixed(2)} ${yForValue(value).toFixed(2)}`;
      })
      .filter(Boolean)
      .join(" ");
  }, [chartRows, xForIndex, yForValue]);

  const yTicks = useMemo(() =>
    Array.from({ length: 4 }, (_, index) => yMin + ((yMax - yMin) * index) / 3),
  [yMin, yMax]);

  const breakdown = useMemo(() => {
    if (!selectedAnomaly) return null;
    const period = selectedAnomaly.period;

    function findOrgUnitByName(facilityName: string): OrgUnitOption | undefined {
      const needle = facilityName.toLowerCase();
      return (orgUnitOptions ?? []).find(
        (o) =>
          o.name?.toLowerCase() === needle ||
          o.l2name?.toLowerCase() === needle ||
          o.l3name?.toLowerCase() === needle ||
          o.l4name?.toLowerCase() === needle ||
          o.l5name?.toLowerCase() === needle ||
          o.l6name?.toLowerCase() === needle ||
          o.l7name?.toLowerCase() === needle
      );
    }

    function facilityOrgPath(facilityName: string): string[] | null {
      const ou = findOrgUnitByName(facilityName);
      if (!ou) return null;
      return [ou.l2name, ou.l3name, ou.l4name, ou.l5name, ou.l6name, ou.l7name].filter(Boolean) as string[];
    }

    function matchesSelectedHierarchy(facilityName: string): boolean {
      if (!anomHierarchy) return true;
      const path = facilityOrgPath(facilityName);
      if (!path) return true;
      const selectedLevels: string[] = [];
      if (anomHierarchy.region) selectedLevels.push(anomHierarchy.region);
      if (anomHierarchy.zone) selectedLevels.push(anomHierarchy.zone);
      if (anomHierarchy.woreda) selectedLevels.push(anomHierarchy.woreda);
      if (anomHierarchy.phcu) selectedLevels.push(anomHierarchy.phcu);
      if (anomHierarchy.level6) selectedLevels.push(anomHierarchy.level6);
      if (anomHierarchy.level7) selectedLevels.push(anomHierarchy.level7);
      if (selectedLevels.length === 0) return true;
      if (path.length < selectedLevels.length) return false;
      for (let i = 0; i < selectedLevels.length; i++) {
        if (path[i]?.toLowerCase() !== selectedLevels[i].toLowerCase()) return false;
      }
      return true;
    }

    const hierarchyLookup = new Map<string, string>();
    (orgUnitOptions ?? []).forEach((ou) => {
      const parts = [ou.l2name, ou.l3name, ou.l4name, ou.l5name, ou.l6name, ou.l7name].filter(Boolean);
      const pathStr = parts.join(" › ");
      const names = [ou.name, ou.l2name, ou.l3name, ou.l4name, ou.l5name, ou.l6name, ou.l7name].filter(Boolean);
      names.forEach((name) => {
        if (!name) return;
        const key = name.toLowerCase();
        if (!hierarchyLookup.has(key)) {
          hierarchyLookup.set(key, pathStr);
        }
      });
    });
    const getHierarchy = (facility: string | undefined) => {
      if (!facility) return "";
      return hierarchyLookup.get(facility.toLowerCase()) || "";
    };
    const single = anomalyRows.filter(
      (r) => r.period === period && r.anomaly_type === selectedAnomaly.anomaly_type && r.facility && matchesSelectedHierarchy(r.facility)
    );
    if (single.length > 0) {
      const topCount = 1;
      return { facilities: single, topCount, topFacilities: [single[0]], total: single.length, highCount: single.filter((r) => r.severity === "high").length, source: "time-series" as const, period, getHierarchy };
    }
    return null;
  }, [selectedAnomaly, anomalyRows, orgUnitOptions, anomHierarchy]);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold text-slate-800">
          {indicator ? `${indicator} — ` : ""}{t("dash.chart.facilitySeries")}
        </h3>
        <p className="text-[10px] text-slate-500">{t("dash.chart.facilitySeriesSub")}</p>
      </div>
      <div className="h-[280px] w-full">
        <div className="h-full w-full overflow-x-auto">
          <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} width={svgWidth} height="100%" role="img" aria-label={t("dash.chart.facilitySeries")}>
            <rect width={svgWidth} height={svgHeight} fill="#ffffff" opacity="0.01" />
            {yTicks.map((tick) => {
              const y = yForValue(tick);
              return (
                <g key={tick}>
                  <line x1={chartMargin.left} x2={svgWidth - chartMargin.right} y1={y} y2={y} stroke="#e2e8f0" strokeDasharray="3 3" />
                  <text x={chartMargin.left - 8} y={y + 3} textAnchor="end" fontSize="9" fill="#475569">
                    {Math.round(tick)}
                  </text>
                </g>
              );
            })}
            <text x={14} y={chartMargin.top + plotHeight / 2} transform={`rotate(-90 14 ${chartMargin.top + plotHeight / 2})`} textAnchor="middle" fontSize="11" fill="#475569">
              {unit}
            </text>
            <path d={linePath("trendExpected")} fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4 4" />
            <path d={linePath("rollingExpected")} fill="none" stroke="#2563eb" strokeWidth="1.5" strokeDasharray="3 5" />
            <path d={linePath("seasonalExpected")} fill="none" stroke="#7c3aed" strokeWidth="1.5" strokeDasharray="2 4" />
            <path d={linePath("value")} fill="none" stroke="#0e7490" strokeWidth="2.5" />
            {chartRows.map((row, index) =>
              index % Math.max(1, Math.ceil(chartRows.length / 10)) === 0 ? (
                <text key={row.period} x={xForIndex(index)} y={svgHeight - 16} textAnchor="middle" fontSize="9" fill="#475569">
                  {row.periodLabel}
                </text>
              ) : null,
            )}
            {anomalies.map((row) => {
              const point = pointForPeriod.get(row.period);
              const value = toFiniteNumber(row.value);
              if (!point || value === null) return null;
              const isSelected = selectedAnomaly?.period === row.period && selectedAnomaly?.anomaly_type === row.anomaly_type;
              const y = yForValue(value);
              return (
                <g key={`${row.period}-${row.anomaly_type}`} style={{ cursor: "pointer" }}>
                  <title>{`${formatPeriodLabel(row.period)} ${row.anomaly_type}: ${value}`}</title>
                  <circle
                    cx={point.x}
                    cy={y}
                    r={12}
                    fill="transparent"
                    onClick={() =>
                      setSelectedAnomaly((prev) =>
                        prev?.period === row.period && prev?.anomaly_type === row.anomaly_type ? null : row,
                      )
                    }
                  />
                  <circle cx={point.x} cy={y} r={isSelected ? 7 : 5} fill={anomalyColor(row.severity)} stroke={isSelected ? "#1e293b" : "#ffffff"} strokeWidth={isSelected ? 2 : 1.5} pointerEvents="none" />
                </g>
              );
            })}
          </svg>
        </div>
      </div>
      {breakdown && (
        <div className="mt-3 rounded-2xl border border-slate-100 bg-blue-50/30 p-4 shadow-soft">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h4 className="text-xs font-semibold text-slate-700">
                {t("dash.breakdown.title", { period: breakdown.period ?? "" })}
              </h4>
              <div className="mt-1 flex gap-3 text-[10px] text-slate-400">
                <span>{t("dash.breakdown.top", { top: breakdown.topCount, total: breakdown.total })}</span>
                {breakdown.highCount > 0 && <span className="font-medium text-red-600">{t("dash.breakdown.high", { n: breakdown.highCount })}</span>}
              </div>
            </div>
            <button
              onClick={() => setSelectedAnomaly(null)}
              className="flex h-6 w-6 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-all"
            >
              <X size={12} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="border-b border-slate-200 text-left text-[9px] font-medium uppercase tracking-wider text-slate-500">
                  <th className="py-1.5 pr-2">{t("dash.table.facility")}</th>
                  <th className="py-1.5 pr-2">{t("dash.table.hierarchy")}</th>
                  <th className="py-1.5 pr-2">{t("dash.table.value")}</th>
                  <th className="py-1.5 pr-2">{t("dash.table.expected")}</th>
                  <th className="py-1.5 pr-2">{t("dash.table.zscore")}</th>
                  <th className="py-1.5 pr-2">{t("dash.table.severity")}</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.topFacilities.map((f) => (
                  <tr key={f.facility} className="border-b border-slate-100 last:border-0">
                    <td className="py-1.5 pr-2 font-medium text-slate-700">{f.facility}</td>
                    <td className="py-1.5 pr-2 text-[9px] text-slate-400">{breakdown.getHierarchy(f.facility)}</td>
                    <td className="py-1.5 pr-2 text-slate-600">{f.value?.toFixed(2) ?? "—"}</td>
                    <td className="py-1.5 pr-2 text-slate-600">{(f as any).baseline ?? (f as any).expected?.toFixed(2) ?? "—"}</td>
                    <td className="py-1.5 pr-2 text-slate-600">{f.score?.toFixed(2) ?? "—"}</td>
                    <td className="py-1.5 pr-2">
                      <span className={`inline-block rounded-lg px-1.5 py-0.5 text-[9px] font-medium uppercase leading-none ${anomalyColor(f.severity).replace("fill-", "")}`}>
                        {formatSeverity(f.severity, t)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* <p className="mt-2 text-[10px] text-slate-500">
            <strong>Top {breakdown.topCount}:</strong> {breakdown.topFacilities.map((f) => `${f.facility}${breakdown.getHierarchy(f.facility) ? ` (${breakdown.getHierarchy(f.facility)})` : ""}`).join(", ")}
          </p> */}
        </div>
      )}
    </div>
  );
});
FacilityTimeChart.displayName = "FacilityTimeChart";

export const FacilityReportedValuesChart: React.FC<{
  rows: FacilityTimeRow[];
  chartType: ReportedChartType;
  onChartTypeChange: (value: ReportedChartType) => void;
}> = ({ rows, chartType, onChartTypeChange }) => {
  const { t } = useLanguage();
  const unit = rows[0]?.unit || "";

  const chartRows = rows.map((row) => ({
    ...row,
    periodLabel: formatPeriodLabel(row.period),
    value: toFiniteNumber(row.value),
  }));
  const svgWidth = Math.max(520, chartRows.length * 48);
  const svgHeight = 210;
  const margin = { top: 12, right: 24, bottom: 32, left: 48 };
  const plotWidth = svgWidth - margin.left - margin.right;
  const plotHeight = svgHeight - margin.top - margin.bottom;
  const values = chartRows.map((row) => row.value).filter((value): value is number => value !== null);
  const maxValue = Math.max(...values, 1);
  const yForValue = (value: number) => margin.top + (1 - value / maxValue) * plotHeight;
  const xForIndex = (index: number) =>
    margin.left + (chartRows.length <= 1 ? plotWidth / 2 : (index / (chartRows.length - 1)) * plotWidth);
  const linePath = chartRows
    .map((row, index) => {
      if (row.value === null) return "";
      return `${index === 0 ? "M" : "L"} ${xForIndex(index).toFixed(2)} ${yForValue(row.value).toFixed(2)}`;
    })
    .filter(Boolean)
    .join(" ");
  const yTicks = Array.from({ length: 4 }, (_, index) => (maxValue * index) / 3);

  return (
    <div className="border-t border-slate-100 pt-2">
      <div className="mb-1 flex items-center justify-between gap-1.5">
        <div>
          <h3 className="text-[10px] font-semibold text-slate-800">{t("dash.chart.reportedValues")}</h3>
          <p className="text-[9px] text-slate-500">{t("dash.chart.reportedValuesSub")}</p>
        </div>
        <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-0.5">
          {(["line", "bar"] as ReportedChartType[]).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => onChartTypeChange(type)}
              className={`h-6 px-3 text-[9px] font-medium capitalize transition-all rounded-lg ${
                chartType === type
                  ? "bg-primary-500 text-white shadow-soft"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t(type === "line" ? "dash.chart.type.line" : "dash.chart.type.bar")}
            </button>
          ))}
        </div>
      </div>
      <div className="h-[210px] w-full min-w-0 overflow-x-auto">
        <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} width={svgWidth} height="100%" role="img" aria-label={t("dash.chart.reportedValues")}>
          <rect width={svgWidth} height={svgHeight} fill="#ffffff" opacity="0.01" />
          {yTicks.map((tick) => {
            const y = yForValue(tick);
            return (
              <g key={tick}>
                <line x1={margin.left} x2={svgWidth - margin.right} y1={y} y2={y} stroke="#e2e8f0" strokeDasharray="3 3" />
                <text x={margin.left - 8} y={y + 3} textAnchor="end" fontSize="9" fill="#475569">
                  {Math.round(tick)}
                </text>
              </g>
            );
          })}
          <text x={14} y={margin.top + plotHeight / 2} transform={`rotate(-90 14 ${margin.top + plotHeight / 2})`} textAnchor="middle" fontSize="9" fill="#475569">
            {unit}
          </text>
          {chartType === "line" && linePath ? (
            <>
              <path d={linePath} fill="none" stroke="#0891b2" strokeWidth="2" />
              {chartRows.map((row, index) =>
                row.value === null ? null : (
                  <circle key={`${row.period}-${index}`} cx={xForIndex(index)} cy={yForValue(row.value)} r="3" fill="#0891b2">
                    <title>{`${row.periodLabel}: ${row.value}`}</title>
                  </circle>
                ),
              )}
            </>
          ) : (
            chartRows.map((row, index) => {
              const value = row.value ?? 0;
              const slot = plotWidth / Math.max(chartRows.length, 1);
              const barWidth = Math.max(12, Math.min(34, slot * 0.58));
              const x = margin.left + index * slot + (slot - barWidth) / 2;
              const y = yForValue(value);
              return (
                <rect key={`${row.period}-${index}`} x={x} y={y} width={barWidth} height={Math.max(1, margin.top + plotHeight - y)} rx="3" fill="#0891b2">
                  <title>{`${row.periodLabel}: ${value}`}</title>
                </rect>
              );
            })
          )}
          {chartRows.map((row, index) =>
            index % Math.max(1, Math.ceil(chartRows.length / 8)) === 0 ? (
              <text key={`${row.period}-label`} x={xForIndex(index)} y={svgHeight - 10} textAnchor="middle" fontSize="9" fill="#475569">
                {row.periodLabel}
              </text>
            ) : null,
          )}
        </svg>
      </div>
    </div>
  );
};
